import store from '@/store/store';
import { ApiBaseService, DataServiceAPI, ApiV2Service } from '@/services/api.base.service';
import { Audit, DataServiceResult, Device, Job, SigfoxDeviceDiagnostic, SUB_ALLOCATE_RESULT, SuccessResponse } from '../types';
import { SearchResponse } from './task.service';
import { SearchDevicesResponse } from './assembly.service';
import Transformation from '../classes/transformation';

export type DeviceSearchResponse = { devices: Device[] } & SearchResponse;

export interface DeviceAddResponse {
  succe: boolean;
  sigfoxJobId: string;
  devices: Device[];
}

export class DeviceService {
  private api: ApiBaseService;
  private dataServiceApi: ApiBaseService;

  constructor() {
    this.api = ApiV2Service;
    this.dataServiceApi = DataServiceAPI;
  }

  public async getDevice(deviceId: number): Promise<Device> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisationId}/device/${deviceId}`);
  }

  public async getDeviceByNetworkId(networkId: string | number): Promise<Device> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisationId}/device/networkId/${networkId}`);
  }


  public async getInventoryDevice(deviceId: number): Promise<Device> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisationId}/inventory/device/${deviceId}`);
  }

  public async getDevices(
    take: number = 0, skip: number = 0, sort: string = '',
    desc: boolean = false, filters: Record<string, Record<string, boolean>> = {},
    projectId: number | null = null, searchNames: string | null = null,
    deviceCategory: string | null = null,
    lanProtocol: 'MBus'| 'Modbus'| null = null,
    includeColumnFilters: boolean = true,
  ): Promise<SearchDevicesResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    let filtersString = '';

    const filterQuery = Transformation.filterMapToQueryString(filters);

    if (filterQuery) {
      filtersString += `&${filterQuery}`;
    }

    if (projectId) {
      filtersString += `&projectId=${projectId}`;
    }
    if (searchNames) {
      filtersString += `&searchNames=${encodeURIComponent(searchNames)}`;
    }

    if (deviceCategory) {
      filtersString += `&deviceCategory=${encodeURIComponent(deviceCategory)}`;
    }
    if (lanProtocol) {
      filtersString += `&lanProtocol=${encodeURIComponent(lanProtocol)}`;
    }
    return this.api.get(
      `organisation/${organisation}/devices?` +
      `take=${take}&skip=${skip}&sort=${sort}&desc=${desc}&includeColumnFilters=${includeColumnFilters}${filtersString}`);
  }

  public async getInventoryDevices(
    take: number = 0, skip: number = 0, sort: string = '',
    desc: boolean = false, filters: Record<string, Record<string, boolean>> = {}, searchNames: string | null = null,
    deviceCategory: string | null = null,
    lanProtocol: 'MBus'| 'Modbus'| null = null,
  ): Promise<SearchDevicesResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    let filtersString = '';

    const filterQuery = Transformation.filterMapToQueryString(filters);

    if (filterQuery) {
      filtersString += `&${filterQuery}`;
    }

    if (searchNames) {
      filtersString += `&searchNames=${encodeURIComponent(searchNames)}`;
    }

    if (deviceCategory) {
      filtersString += `&deviceCategory=${encodeURIComponent(deviceCategory)}`;
    }
    if (lanProtocol) {
      filtersString += `&lanProtocol=${encodeURIComponent(lanProtocol)}`;
    }
    return this.api.get(
      `organisation/${organisation}/inventory/devices?` +
      `take=${take}&skip=${skip}&sort=${sort}&desc=${desc}&includeColumnFilters=true${filtersString}`);
  }

  public async saveDevices(devices: any[], deviceVersionId: number): Promise<DeviceAddResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/deviceVersion/${deviceVersionId}/devices`, {devices});
  }

  public async saveInventoryDevices(devices: any[], deviceVersionId: number): Promise<DeviceAddResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/deviceVersion/${deviceVersionId}/inventory/devices`, {devices});
  }

  public async updateDevice(id: number, payload: object): Promise<Device> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisation}/device/${id}`, payload);
  }

  public async searchDeviceTags(search: string): Promise<string[]> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisation}/devices/tags/search?search=${search}`);
  }

  public async createDeviceTag(id: number, payload: {
    tag: string;
    value: string
  }): Promise<Device> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/devices/${id}/tags`, payload);
  }

  public async updateDeviceTag(id: number, tagId: number, payload: {
    tag: string;
    value: string
  }): Promise<Device> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisation}/devices/${id}/tags/${tagId}`, payload);
  }

  public async deleteDeviceTag(id: number, tagId: number): Promise<Device> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.delete(`organisation/${organisation}/devices/${id}/tags/${tagId}`);
  }

  public async mountDevice(id: number, payload: object): Promise<{device: Device | null, result: SUB_ALLOCATE_RESULT}> {
    const organisation = store.getters.activeOrganisation[0].id;
    const response = await this.api.post<Device>(`organisation/${organisation}/device/${id}/mount`, payload,
      {
        noToastFilters: [ {status: 422, gives: (resp: any) => ({ data: { subscription_error_code: resp.data.subscription_error_code }}) } ],
      });
    return this.processSubscriptionResult(response);
  }

  public async moveDevice(id: number, payload: { locationId?: number; positionId?: number }) {
    const organisation = store.getters.activeOrganisation[0].id;
    const response = await this.api.post<Device>(`organisation/${organisation}/device/${id}/move`, payload, {
      noToastFilters: [ {status: 422, gives: (resp: any) => ({ data: { subscription_error_code: resp.data.subscription_error_code }}) } ],
    });
    return this.processSubscriptionResult(response);
  }

  public async checkMountDevices(payload: { deviceIds: number[], locationId: number }): Promise<void> {
    const organisation = store.getters.activeOrganisation[0].id;
    await this.api.post<Device>(`organisation/${organisation}/devices/mount/check`, payload);
  }

  public async unmountDevice(id: number): Promise<Device> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/device/${id}/unmount`, {});
  }

  public async unmountDevices(ids: number[]): Promise<Device[]> {
    const payload = {
      deviceIds: ids,
    };
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/devices/unmount`, payload);
  }

  public async unmountTestDevice(id: number): Promise<Device> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/device/${id}/test/unmount`, {});
  }

  public async unmountTestDevices(ids: number[]): Promise<Device[]> {
    const payload = {
      deviceIds: ids,
    };
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/devices/test/unmount`, payload);
  }

  public async assignDevices(payload: object): Promise<Device[]> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/devices/assign`, payload);
  }

  public async assignDevicesBulk(payload: object): Promise<{ job_id: string }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/devices/assign/bulk`, payload);
  }
  public async disposeDevicesBulk(payload: object): Promise<{ job_id: string }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/devices/dispose/bulk`, payload);
  }
  public async testDevicesBulk(payload: object): Promise<{ job_id: string }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/devices/test/bulk`, payload);
  }
  public async unassignDevicesBulk(payload: object): Promise<{ job_id: string }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/devices/unassign/bulk`, payload);
  }

  public async unAssignDevices(devices: any[]): Promise<Device[]> {
    const payload: any = {devices};
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/devices/unassign`, payload);
  }

  public async deleteDevices(devices: any[], deleteReason: string): Promise<SuccessResponse> {
    const payload = {deviceIds: devices, deleteReason};
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.delete(`organisation/${organisation}/devices`, payload);
  }

  public async checkUnique(deviceId?: string, networkId?: string, bearer?: string): Promise<{ valid: { deviceId: boolean, sigfoxId: boolean } }> {
    const organisation = store.getters.activeOrganisation[0].id;
    const queryParams = new URLSearchParams();
    let queryString = '';

    if (deviceId) {
      queryParams.append('deviceId', deviceId.toString());
    }
    if (networkId) {
      queryParams.append('sigfoxId', networkId.toString());
    }
    if (bearer) {
      queryParams.append('bearer', bearer.toString());
    }
    queryString = `?${queryParams.toString()}`;
    return this.api.get(`organisation/${organisation}/device/check-unique${queryString}`);
  }

  public async checkManyUnique(devices: any[]): Promise<{ errors: string[] }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/devices/check-unique`, devices);
  }

  public async getDeviceMessages(device: any, start: number = 0, limit: number = 10, after?: string, ignoreErrors: boolean = false): Promise<DataServiceResult> {
    const organisation = store.getters.activeOrganisation[0].slug;
    const deviceSlug = device.device_version.slug;
    const deviceId = device.chip.identifier;
    return this.dataServiceApi.get(`messages/${organisation}/${deviceSlug}/${deviceId}?limit=${limit}&timeOrder=desc`, ignoreErrors);
  }

  public async getDeviceMessagesByInterface(networkId: string, interfaceSlug: string, start: number = 0, limit: number = 10): Promise<DataServiceResult> {
    const organisation = store.getters.activeOrganisation[0].slug;
    return this.dataServiceApi.get(`interfaces/${organisation}/${interfaceSlug}/${networkId}?limit=${limit}&timeOrder=desc`);
  }

  public async getDeviceEvents(device: any, offset: number = 0, limit: number = 10, before?: string, since?: string, ignoreErrors: boolean = false): Promise<DataServiceResult> {
    const organisation = store.getters.activeOrganisation[0].slug;
    const deviceSlug = device.device_version.slug;
    const deviceId = device.chip.identifier;
    return this.dataServiceApi.get(`events/${organisation}/${deviceSlug}/${deviceId}?limit=${limit}${offset ? `&offset=${offset}` : ''}${before ? `&before=${encodeURIComponent(before)}` : ''}${since ? `&since=${encodeURIComponent(since)}` : ''}&timeOrder=desc`, ignoreErrors);
  }

  public async getDeviceAudit(deviceId: number): Promise<Audit[]> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.get(`audit/organisation/${organisation}/device/${deviceId}`);
  }

  public async changeDeviceType(deviceId: number, deviceVersionId: number): Promise<Device> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisation}/device/${deviceId}/change-type`, {deviceVersionId});
  }

  public async getDeviceSigfoxDiagnostics(deviceId: number): Promise<SigfoxDeviceDiagnostic> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisation}/sigfox/device-diagnostic/${deviceId}`, { ignoreErrors: true });
  }


  public async decommissionDevice(deviceId: number): Promise<SuccessResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisation}/devices/${deviceId}/decommission`, {});
  }

  public async decommissionDevices(deviceIds: number[]): Promise<{ job_id: string }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/devices/decommission/bulk`, { device_ids: deviceIds});
  }

  public async stockDevice(deviceId: number): Promise<SuccessResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisation}/devices/${deviceId}/stock`, {});
  }

  public async stockDevices(deviceIds: number[]): Promise<{ job_id: string }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/devices/stock/bulk`, { device_ids: deviceIds});
  }

  public async activateDevice(deviceId: number, useActiveSubs: boolean, managedByDaizy: boolean | null): Promise<SUB_ALLOCATE_RESULT> {
    const organisation = store.getters.activeOrganisation[0].id;
    const response = await this.api.put<Device>(`organisation/${organisation}/devices/${deviceId}/activate`, { useActivatedSubscriptions: useActiveSubs, managedByDaizy },
      {
        noToastFilters: [ {status: 422, gives: (resp: any) => ({ data: { subscription_error_code: resp.data.subscription_error_code }}) } ],
      });
    return this.processSubscriptionResult(response).result;
  }

  public async activateDevices(deviceIds: number[], useActiveSubs: boolean): Promise<{allPassed: boolean, errors: Array<{ device_id: number, error: Error }>}> {
    if (deviceIds.length === 1) {
      throw new Error('Do not use bulk endpoint for single device subscriptions!');
    }
    const organisation = store.getters.activeOrganisation[0].id;
    try {
      const result = await this.api.put<any>(`organisation/${organisation}/devices/activate`, { device_ids: deviceIds, useActivatedSubscriptions: useActiveSubs }, {ignoreError: true});
      if (!result.success) {
        throw result;
      }
      return { allPassed: true, errors: [] };
    } catch (e) {
      const err = e as any;
      if (!err.device_errors) {
        throw err;
      }
      return { allPassed: false, errors: err.device_errors };
    }
  }
  public async activateDevicesBulk(deviceIds: number[], useActiveSubs: boolean, managedByDaizy: boolean): Promise<{ job_id: string }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/devices/activate/bulk`, { device_ids: deviceIds, useActivatedSubscriptions: useActiveSubs, managedByDaizy });
  }

  public async unmountDevicesBulk( deviceIds: number[] ): Promise<{ job_id: string }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/devices/unmount/bulk`, { device_ids: deviceIds});
  }
  public async unmountTestDevicesBulk( deviceIds: number[] ): Promise<{ job_id: string }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/devices/unmounttest/bulk`, { device_ids: deviceIds});
  }

  public async setManagedByDaizy(deviceId: number, managedByDaizy: boolean): Promise<void> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisation}/device/${deviceId}/managed`, { managedByDaizy });
  }

  public async setAlertingEnabled(deviceId: number, alertingEnabled: boolean): Promise<void> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/device/${deviceId}/alerting`, { enabled: alertingEnabled });
  }

  public async pairSim(deviceId: number, options: { simId?: number, thirdPartySim?: boolean }): Promise<Device> {
    const organisation = store.getters.activeOrganisation[0].id;
    const payload: { sim_card_id?: number, third_party_sim?: boolean } = { };
    if (options.thirdPartySim) {
      payload.third_party_sim = true;
    } else {
      payload.sim_card_id = options.simId;
    }
    return await this.api.post(`organisation/${organisation}/device/${deviceId}/pair`, payload, { ignoreErrors: true });
  }

  public async pairSims(pairs: Array<{deviceId: number, simId?: number, thirdPartySim?: boolean}>): Promise<Device[]> {
    const organisation = store.getters.activeOrganisation[0].id;
    const pairsTransformed = pairs.map((p) => {
      const payload: { device_id: number, sim_card_id?: number, third_party_sim?: boolean } = {
        device_id: p.deviceId,
      };
      if (p.thirdPartySim) {
        payload.third_party_sim = true;
      } else {
        payload.sim_card_id = p.simId;
      }
      return payload;
    });
    return this.api.post(`organisation/${organisation}/devices/pair`, pairsTransformed);
  }

  public async unpairSim(deviceId: number): Promise<Device> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisation}/device/${deviceId}/unpair`, {});
  }

  public async unpairSims(deviceIds: number[]): Promise<Device[]> {
    const organisation = store.getters.activeOrganisation[0].id;
    const payload = {
      device_ids: deviceIds,
    };
    return this.api.post(`organisation/${organisation}/devices/unpair`, payload);
  }

  public async getShadowByNetworkId(networkId: string): Promise<any> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisation}/device/${networkId}/shadow`);
  }

  public async testDevices(ids: number[], ignoreSubscriptionFailure: boolean = false): Promise<Device[]> {
    const organisation = store.getters.activeOrganisation[0].id;
    let config: any;
    if (ignoreSubscriptionFailure) {
      config = {
        noToastFilters: [
          {
            errorMessage: 'No subscriptions available',
          },
        ],
      };
    }
    return this.api.post(`organisation/${organisation}/devices/test`, {deviceIds: ids}, config);
  }

  public async export(projectId: number | null, queryData: any, searchName: string): Promise<{ job_id: string }> {
    const organisation = store.getters.activeOrganisationId;

    const filters = Transformation.queryDataToExportPayload(queryData.filters);

    const eventPayload = {
      ...(projectId ? { project_id: projectId } : null),
      ...(filters ? { filters } : null),
      sort_params: {
        ...(queryData.sort ? {
          order: {
            [queryData.sort]: queryData.desc ? 'DESC' : 'ASC',
          },
        } : null),
      },
      ...(searchName ? { search_name: searchName } : null),
    };
    return this.api.post(`organisation/${organisation}/devices/export`, eventPayload);
  }

  public async exportInventory(queryData: any, searchName: string): Promise<{ job_id: string }> {
    const organisation = store.getters.activeOrganisationId;

    const filters = Transformation.queryDataToExportPayload(queryData.filters);

    const eventPayload = {
      ...(filters ? { filters } : null),
      sort_params: {
        ...(queryData.sort ? {
          order: {
            [queryData.sort]: queryData.desc ? 'DESC' : 'ASC',
          },
        } : null),
      },
      ...(searchName ? { search_name: searchName } : null),
    };
    return this.api.post(`organisation/${organisation}/inventory/export`, eventPayload);
  }

  public exportMessagesCsv(deviceId: number, options: Record<string, any>): Promise<{ job_id: string }> {
    const organisation = store.getters.activeOrganisationId;
    return this.api.post(`organisation/${organisation}/device/${deviceId}/export`, { options });
  }

  public async changeDeviceLns(deviceId: number, lnsId: number | null = null): Promise<Device> {
    const organisationId = store.getters.activeOrganisationId;
    return this.api.put(`organisation/${organisationId}/device/${deviceId}/network-server`, { lns_id: lnsId });
  }

  public async transferDevice(deviceId: number, body: { sourceOrganisationId: number; destinationOrganisationId: number }): Promise<{ success: string }> {
    const organisation = store.getters.activeOrganisationId;
    return this.api.put(`organisation/${organisation}/devices/${deviceId}/transfer`, body);
  }


  public async transferDevices(body: { sourceOrganisationId: number; destinationOrganisationId: number, deviceIds: number[] }): Promise<{ job_id: string }> {
    const organisation = store.getters.activeOrganisationId;
    return this.api.put(`organisation/${organisation}/devices/transfer`, body);
  }

  public async canTransferDevicesCheck(body: { sourceOrganisationId: number; destinationOrganisationId: number, deviceIds: number[] }): Promise<{ result: boolean; reason?: string }> {
    const organisation = store.getters.activeOrganisationId;
    return this.api.post(`organisation/${organisation}/devices/canTransfer`, body);
  }

  public async getDeviceRadioHistory(deviceId: number, date?: string): Promise<any> {
    const organisation = store.getters.activeOrganisationId;
    let uri = `organisation/${organisation}/devices/${deviceId}/radioData/history`;

    if (date) {
      uri += `?date=${encodeURIComponent(date)}`;
    }

    return this.api.get(uri);
  }

  private processSubscriptionResult(response: Device | { subscription_error_code: number }): {device: Device | null, result: SUB_ALLOCATE_RESULT} {
    const result: {device: Device | null, result: SUB_ALLOCATE_RESULT} = {
      device: null,
      result: 'ok',
    };
    if ('subscription_error_code' in response) {
      switch (response.subscription_error_code) {
        case 0:
        case -2:
          result.result = 'no_sub';
          break;
        case -1:
          result.result = 'no_sim';
          break;
        case -3:
          result.result = 'not_expired';
          break;
        case -4:
          result.result = 'is_sigfox';
          break;
        case -5:
          result.result = 'no_expiration';
          break;
        case -6:
          result.result = 'no_allocations';
          break;
        case -7:
          result.result = 'allocation_limit_reached';
          break;
        case -8:
          result.result = 'resub_not_supported';
          break;
        default:
          throw new Error('Invalid subscription_error_code');
      }
    } else if ('id' in response) {
      result.device = response;
    }

    return result;
  }
}

export default new DeviceService();
